import clsx from 'clsx';
import React, { useState } from 'react';

import Button from '@components/Button';
import { PrimaryButton, SecondaryButton } from '@components/Button/variants';
import SvgInstagramBlack from '@components/Images/InstagramBlack';
import SvgIconFaceBook from '@components/Images/Rebranding/IconFacebook';
import SvgIconLinkedIn from '@components/Images/Rebranding/IconLinkedIn';
import IconPlus from '@components/Images/Rebranding/IconPlus';
import SvgIconX from '@components/Images/Rebranding/IconX';
import { EmployeeProps } from '@utils/aboutInterfaces';

const Employee = ({ canSendMessage, className, member, onMessage }: EmployeeProps) => {
  const [isActive, setActive] = useState(false);
  const { name, title, bio, twitter, linkedinUrl, facebookUrl, instagram, recentArticle } = member;

  const buildSocialMediaLinks = () => {
    const socialMediaLinks = [];
    const iconClass =
      'items-center justify-center transition-colors text-black  hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta';

    // Check if the social media profiles exist and push them into the array
    if (twitter)
      socialMediaLinks.push({
        link: `https://www.x.com/${twitter}`,
        icon: <SvgIconX className={clsx(iconClass, 'w-[21px]')} />,
      });
    if (linkedinUrl)
      socialMediaLinks.push({
        link: linkedinUrl,
        icon: <SvgIconLinkedIn className={clsx(iconClass, 'w-[21px]')} />,
      });
    if (facebookUrl)
      socialMediaLinks.push({
        link: facebookUrl,
        icon: <SvgIconFaceBook className={clsx(iconClass, 'w-12')} />,
      });
    if (instagram)
      socialMediaLinks.push({
        link: `https://www.instagram.com/${instagram.replace('@', '')}`,
        icon: (
          <SvgInstagramBlack className={clsx(iconClass, 'w-24 hover:stroke-primary-magenta')} />
        ),
      });
    return socialMediaLinks;
  };

  return (
    <div className="mt-24 w-full">
      <div
        className={clsx(
          'group flex flex-col items-start gap-10 border-0 border-b border-solid border-grayscale-beige pb-32 hover:text-primary-magenta',
          className
        )}
      >
        <div
          className="flex w-full cursor-pointer items-center justify-between text-left hover:text-primary-magenta"
          onClick={() => setActive(!isActive)}
          data-testid="Employee"
        >
          <h4
            className={clsx(
              'my-0 font-suisse-works text-body-md font-normal no-underline group-hover:text-primary-magenta',
              {
                'text-primary-magenta': isActive, // Change color if active
              }
            )}
          >
            {name}
            <span className="block text-left font-suisse-intl text-icon-sm text-grayscale-gray hover:text-grayscale-gray">
              {title}
            </span>
          </h4>
          <IconPlus
            className={clsx(
              'w-[27px] transform stroke-[0.5] transition-transform group-hover:fill-[#f32a52]',
              {
                'rotate-45 text-primary-magenta': isActive,
                'fill-grayscale-charcoal': !isActive,
              }
            )}
          />
        </div>
        {isActive && (
          <>
            <div className="mt-8 font-suisse-neue text-body-md text-black group-hover:text-black">
              {bio}
            </div>
            {(twitter || linkedinUrl || facebookUrl || instagram) && (
              <div className="mt-24 flex gap-32">
                {buildSocialMediaLinks().map((link) => (
                  <Button
                    as="link"
                    href={link.link}
                    key={link.link}
                    className="flex items-center gap-8"
                    data-test="social-media-link"
                  >
                    {link.icon}
                  </Button>
                ))}
              </div>
            )}
            {(canSendMessage || recentArticle) && (
              <div className="mt-32 flex w-full gap-8">
                {canSendMessage && (
                  <PrimaryButton
                    as="button"
                    className="flex-1 !px-24 py-8 font-suisse-intl text-button-sm focus-visible:ring-0 max-sm:hover:!bg-transparent"
                    onClick={onMessage}
                    data-test="message-button"
                  >
                    Message
                  </PrimaryButton>
                )}
                {recentArticle && (
                  <SecondaryButton
                    as="link"
                    href={member.recentArticle}
                    data-test="recent-article-link"
                    size="small"
                    className="flex-1 !px-24 py-8 font-suisse-intl text-button-sm focus:bg-transparent focus-visible:ring-0 md:px-24"
                  >
                    View recent articles
                  </SecondaryButton>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Employee;
